import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ClubbieButton from "../../components/ClubbieButton/ClubbieButton";
import ClubbieDropdown from "../../components/ClubbieDropdown/ClubbieDropdown";
import DatePicker from "../../components/DatePicker/DatePicker";
import MultiSelectDropdown from "../../components/MultiSelectDropdown/MultiSelectDropdown";
import { updateTournament } from "../../redux/slices/tournamentSlice";
import { fixedValues } from "../../utils/fixedValues";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import { tournamentWizardStepCompleted } from "../../utils/analytics";
import { WIZARD_STEP } from "../../utils/analyticsEvents";
import "./SetMatchSchedule.css";

const INITIAL_SCHEDULE = {
  startDate: "",
  endDate: "",
  matchDays: [],
  koTimePerMatch: "90",
  matchTimings: [],
  startTime: "09:00",
  dayEndTime: "21:00",
};

const DROPDOWN_OPTIONS = {
  matchDays: [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ],
  koTimePerMatch: ["20", "30", "45", "60", "90", "120"],
  matchTimings: Array.from(
    { length: 24 },
    (_, i) => `${String(i).padStart(2, "0")}:00`
  ),
};

const useScheduleState = (selectedTournament) => {
  const [schedule, setSchedule] = useState(INITIAL_SCHEDULE);

  useEffect(() => {
    if (selectedTournament?.matchSchedule) {
      const { startDate, endDate, matchDays, koTimePerMatch, matchTimings, startTime, dayEndTime } =
        selectedTournament.matchSchedule;
      setSchedule({
        startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
        endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
        matchDays: matchDays || [],
        koTimePerMatch: koTimePerMatch?.toString() || "90",
        matchTimings: matchTimings || [],
        startTime: startTime || "09:00",
        dayEndTime: dayEndTime || "21:00",
      });
    }
  }, [selectedTournament]);

  return [schedule, setSchedule];
};

const useDropdownState = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown) {
        const activeDropdownRef = dropdownRefs.current[openDropdown];
        if (activeDropdownRef && !activeDropdownRef.contains(event.target)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  return { openDropdown, setOpenDropdown, dropdownRefs };
};

const useValidation = (schedule, selectedTournament) => {
  const [errors, setErrors] = useState({});

  const validateSchedule = useCallback(() => {
    const newErrors = {};
    if (!schedule.startDate) newErrors.startDate = "Start date is required";
    if (
      !schedule.endDate &&
      selectedTournament?.durationType !== "SINGLE_DAY"
    ) {
      newErrors.endDate = "End date is required";
    }
    if (!schedule.matchDays.length)
      newErrors.matchDays = "At least one match day is required";
    if (!schedule.matchTimings.length)
      newErrors.matchTimings = "At least one match timing is required";

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  }, [schedule, selectedTournament]);

  return { errors, validateSchedule };
};

const SetMatchSchedule = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedTournament = useSelector(
    (state) => state.tournament.selectedTournament
  );

  const [schedule, setSchedule] = useScheduleState(selectedTournament);
  const { openDropdown, setOpenDropdown } = useDropdownState();
  const { errors, validateSchedule } = useValidation(
    schedule,
    selectedTournament
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = useCallback(
    (field, value, isMulti = false) => {
      setSchedule((prev) => {
        if (isMulti) {
          const currentValues = prev[field] || [];
          const isAlreadySelected = currentValues.includes(value);

          const newValues = isAlreadySelected
            ? currentValues.filter((item) => item !== value)
            : [...currentValues, value];

          return {
            ...prev,
            [field]: newValues,
          };
        }

        // For single-select fields
        const newSchedule = {
          ...prev,
          [field]: value,
        };

        // If it's a Single-day Competition and start date is being set, set end date to the same value
        if (
          field === "startDate" &&
          selectedTournament?.durationType === "SINGLE_DAY"
        ) {
          newSchedule.endDate = value;
          newSchedule.matchDays = [moment(value).format("dddd")];
        }

        return newSchedule;
      });

      if (!isMulti) {
        setOpenDropdown(null);
      }
    },
    [setSchedule, setOpenDropdown, selectedTournament]
  );

  const handleNext = useCallback(async () => {
    if (!validateSchedule()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      if (selectedTournament?.id) {
        const result = await dispatch(
          updateTournament({
            id: selectedTournament.id,
            updateData: {
              matchSchedule: {
                startDate: moment(schedule.startDate).toISOString(),
                endDate: moment(schedule.endDate).toISOString(),
                matchDays: schedule.matchDays,
                // koTimePerMatch: parseInt(schedule.koTimePerMatch),
                matchTimings: schedule.matchTimings,
                startTime: schedule.startTime,
                dayEndTime: schedule.dayEndTime,
              },
            },
          })
        );

        if (result.meta.requestStatus === "fulfilled") {
          tournamentWizardStepCompleted({
            stepName: WIZARD_STEP.SET_MATCH_SCHEDULE,
            tournamentId: selectedTournament?.id,
          });
          toast.success("Match schedule updated successfully!");
          const nextRoute =
            DASHBOARD_STACK.CREATE_TOURNAMENT.CREATE_TOURNAMENT_DIVISION;
          navigate(nextRoute);
        } else {
          throw new Error("Failed to update match schedule");
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to update match schedule");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate, schedule, selectedTournament, validateSchedule]);

  const dateFields = useMemo(
    () =>
      [
        {
          field: "startDate",
          label: "What is the start date?",
          error: errors.startDate,
        },
        {
          field: "endDate",
          label: "What is the end date?",
          error: errors.endDate,
        },
      ].filter(
        (field) =>
          field.field === "startDate" ||
          selectedTournament.durationType !== "SINGLE_DAY"
      ),
    [errors, selectedTournament]
  );

  return (
    <>
      <div className="input-section">
        <p>Set Match Schedule</p>
        <div className="schedule-container">
          {dateFields.map(({ field, label, error }) => (
            <div className="schedule-field" key={field}>
              <label>
                {label} <span className="required">*</span>
              </label>
              <div className="react-calendar-wrapper">
                <DatePicker
                  value={schedule[field]}
                  onChange={(value) => handleChange(field, value)}
                  className={`date-input ${error ? "error" : ""}`}
                  type="date"
                  disablePastDates={false}
                />
              </div>
              {error && <div className="error-message">{error}</div>}
            </div>
          ))}
          {selectedTournament.durationType !== "SINGLE_DAY" && (
            <MultiSelectDropdown
              name="matchDays"
              label="What days will matches be played?"
              value={schedule.matchDays}
              options={DROPDOWN_OPTIONS.matchDays}
              onChange={(value) => handleChange("matchDays", value)}
              error={errors.matchDays}
              required
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
            />
          )}
          <MultiSelectDropdown
            name="matchTimings"
            label="Select match timings"
            value={schedule.matchTimings}
            options={DROPDOWN_OPTIONS.matchTimings}
            onChange={(value) => handleChange("matchTimings", value)}
            error={errors.matchTimings}
            required
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
          />

          {/* Day start and end times */}
          <div className="schedule-field">
            <label>Day start time</label>
            <input
              type="time"
              value={schedule.startTime || "09:00"}
              onChange={(e) => handleChange("startTime", e.target.value)}
              className="date-input"
            />
          </div>
          <div className="schedule-field">
            <label>Day end time</label>
            <input
              type="time"
              value={schedule.dayEndTime || "21:00"}
              onChange={(e) => handleChange("dayEndTime", e.target.value)}
              className="date-input"
            />
          </div>
        </div>
      </div>

      <ClubbieButton
        className="next-button"
        onClick={handleNext}
        isLoading={isLoading}
      >
        Next
      </ClubbieButton>
    </>
  );
};

export default SetMatchSchedule;
